// Module: LoadingSpinner
// -----------------------------------------------------------------
// Receives:  a message and where the wait happens (page or screen)
// Produces:  the app's loading indicator, in the workspace palette
// Owns:      the show delay that keeps quick loads from flashing
// Does NOT:  the indicator's markup (→ et-ui LoadingSpinner), which is
//            restyled through its class hooks in workspace.css
// -----------------------------------------------------------------
import { useEffect, useState } from "react";
import { LoadingSpinner as EtLoadingSpinner } from "@ever-tutor/et-ui";
import "@/components/workspace/workspace.css";

/** A load that settles sooner never shows the indicator at all. */
const SHOW_DELAY_MS = 300;

interface LoadingSpinnerProps {
  message?: string;
  /**
   * "page" waits inside the content area and leaves the workspace shell in
   * place; "screen" covers the app while no shell exists yet (auth checks).
   */
  placement?: "page" | "screen";
}

const LoadingSpinner = ({
  message = "Loading...",
  placement = "page",
}: LoadingSpinnerProps) => {
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const timer = window.setTimeout(() => setIsVisible(true), SHOW_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, []);

  return (
    <EtLoadingSpinner
      message={message}
      variant={placement === "screen" ? "fullscreen" : "inline"}
      aria-label={message}
      className={`et-product-surface et-loading et-loading-${placement}${
        isVisible ? " is-visible" : ""
      }`}
      spinnerClassName="et-loading-ring"
    />
  );
};

export default LoadingSpinner;
